/**
 * CalendarTile - Upcoming calendar events (next 7 days)
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { format, parseISO, isToday, isTomorrow } from 'date-fns';
import { CalendarIcon } from '@heroicons/react/24/outline';
import { Panel, Badge, Spinner } from '../../design/primitives';
import { COLORS, SPACING, TYPOGRAPHY } from '../../design/tokens';

interface CalendarEvent {
  id: number;
  title: string;
  start: string;
  end: string;
  event_type?: string;
  is_meeting?: boolean;
}

interface CalendarTileProps {
  events: CalendarEvent[];
  loading?: boolean;
}

const CalendarTile: React.FC<CalendarTileProps> = ({ events, loading = false }) => {
  const formatTime = (dateStr: string): string => {
    const date = parseISO(dateStr);
    return format(date, 'h:mm a');
  };

  const getDayLabel = (dateStr: string): string => {
    const date = parseISO(dateStr);
    if (isToday(date)) return 'Today';
    if (isTomorrow(date)) return 'Tomorrow';
    return format(date, 'EEE, MMM d');
  };

  const groupedEvents = events.reduce((acc, event) => {
    const dayKey = format(parseISO(event.start), 'yyyy-MM-dd');
    if (!acc[dayKey]) acc[dayKey] = [];
    acc[dayKey].push(event);
    return acc;
  }, {} as Record<string, CalendarEvent[]>);

  const sortedDays = Object.keys(groupedEvents).sort();

  if (loading) {
    return (
      <Panel style={{ minHeight: '140px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Spinner size="md" />
      </Panel>
    );
  }

  return (
    <Panel>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: SPACING[12] }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: SPACING[8] }}>
          <CalendarIcon style={{ width: '18px', height: '18px', color: COLORS.status.info }} />
          <span style={{
            fontFamily: TYPOGRAPHY.headings.fontFamily,
            fontSize: TYPOGRAPHY.headings.sizes.h3,
            fontWeight: TYPOGRAPHY.headings.weight,
            color: COLORS.text.primary,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
          }}>
            Calendar
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: SPACING[8] }}>
          {events.length > 0 && (
            <Badge variant="default" size="sm">{events.length}</Badge>
          )}
          <Link to="/calendar" style={{ fontSize: TYPOGRAPHY.body.sizes.tiny, color: COLORS.accent.default, textDecoration: 'none' }}>
            View All
          </Link>
        </div>
      </div>

      {events.length === 0 ? (
        <div style={{ color: COLORS.text.tertiary, fontSize: TYPOGRAPHY.body.sizes.default, textAlign: 'center', padding: SPACING[16] }}>
          No upcoming events
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING[12] }}>
          {sortedDays.map((dayKey) => (
            <div key={dayKey}>
              <div style={{
                fontSize: TYPOGRAPHY.body.sizes.small,
                fontWeight: '600',
                color: COLORS.text.secondary,
                marginBottom: SPACING[4],
              }}>
                {getDayLabel(groupedEvents[dayKey][0].start)}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING[4] }}>
                {groupedEvents[dayKey].map((event) => (
                  <Link
                    key={event.id}
                    to="/calendar"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: SPACING[8],
                      padding: SPACING[8],
                      backgroundColor: COLORS.bg.hover,
                      borderRadius: '4px',
                      textDecoration: 'none',
                      borderLeft: `3px solid ${event.is_meeting ? COLORS.status.info : COLORS.accent.default}`,
                    }}
                  >
                    <span style={{ fontSize: TYPOGRAPHY.body.sizes.small, color: COLORS.text.tertiary, minWidth: '60px' }}>
                      {formatTime(event.start)}
                    </span>
                    <span style={{
                      flex: 1,
                      fontSize: TYPOGRAPHY.body.sizes.default,
                      color: COLORS.text.primary,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}>
                      {event.title}
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </Panel>
  );
};

export default CalendarTile;
